import { Button, Card } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useState } from 'react';

import { useKalosLogoQuery } from '../brand';
import { helcimCardsOnFileQueryKey } from './cardOnFileQueryKey';
import { createHelcimVerifyCheckoutSession } from './createHelcimVerifyCheckoutSession';
import { HelcimCheckout } from './HelcimCheckout';

export interface HelcimCardTokenizePanelProps {
  userId: number;
  grpcBaseUrl: string;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * Loads a verify (tokenize-only) Helcim session and opens the Pay.js iframe.
 */
export const HelcimCardTokenizePanel: FC<HelcimCardTokenizePanelProps> = ({
  userId,
  grpcBaseUrl,
  onDone,
  onCancel,
}) => {
  const kalosLogo = useKalosLogoQuery(grpcBaseUrl);
  const queryClient = useQueryClient();
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setInitError(null);
      setCheckoutToken(null);
      const result = await createHelcimVerifyCheckoutSession({
        apiBaseUrl: grpcBaseUrl,
        userId,
      });
      if (cancelled) return;
      if (!result.ok) {
        setInitError(result.error);
        setLoading(false);
        return;
      }
      setCheckoutToken(result.checkoutToken);
      setLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [grpcBaseUrl, userId]);

  const handleSuccess = useCallback(async () => {
    const key = helcimCardsOnFileQueryKey(userId);
    for (let i = 0; i < 3; i++) {
      await queryClient.invalidateQueries({ queryKey: key });
      await new Promise((r) => setTimeout(r, 600));
    }
    onDone();
  }, [onDone, queryClient, userId]);

  if (loading) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center gap-3 py-10">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-hidden />
        <p className="text-sm">Preparing secure card form…</p>
      </div>
    );
  }

  if (initError || !checkoutToken) {
    return (
      <Card className="border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-red-800">Could not start card capture</p>
            <p className="mt-1 text-sm text-red-700">{initError ?? 'Missing checkout token'}</p>
            <Button type="button" variant="outline" className="mt-4" onClick={onCancel}>
              Close
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <HelcimCheckout
      logoUrl={kalosLogo.data}
      checkoutToken={checkoutToken}
      amount={0}
      description="Verify card on file"
      mode="tokenize"
      presentation="iframeOnly"
      autoOpenPayIframe
      suppressInternalSuccessUI
      showCancelButton={false}
      onSuccess={() => void handleSuccess()}
      onCancel={onCancel}
    />
  );
};
